import {
  isValid,
  format,
  parse,
  differenceInWeeks,
  isDate,
  parseISO,
} from 'date-fns';
import * as dateFnsTz from 'date-fns-tz';
import {
  centralTimezone,
  dateFormat,
  dateFormatShort,
  defaultTimeZone,
  gmt0timezone,
  lgmDateFormatFns,
  longDateTimeFormat,
  lrpDateFormat,
} from './constants';

const { utcToZonedTime } = dateFnsTz;

/**
 * @deprecated Use `@harvestiq/dayjs` instead.
 */
export const localization = {
  locale: 'en-US',
  setLocale(locale) {
    this.locale = locale;
  },
};

/**
 * @deprecated Use `@harvestiq/dayjs` instead.
 */
export const zonedDate = (date, timeZone = defaultTimeZone) =>
  utcToZonedTime(isValid(date) ? date : new Date(date), timeZone);

const isEmpty = (input) =>
  input === null || input === undefined || input === '';
/**
 * @deprecated Use `@harvestiq/dayjs` instead.
 */
export const shortDateTime = (input, locale = localization.locale) => {
  if (isEmpty(input)) {
    return '';
  }
  const date = isValid(input) ? input : new Date(input);

  const formatter = new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
  });

  return formatter.format(date);
};

/**
 * @deprecated Use `@harvestiq/dayjs` instead.
 */
export const newZonedDate = (date) => new Date(zonedDate(date, gmt0timezone));

/**
 * @deprecated Use `@harvestiq/dayjs` instead.
 */
export const parseMarketingMonth = (marketingMonth) =>
  parse(marketingMonth, lgmDateFormatFns, new Date());

/**
 * @deprecated Use `@harvestiq/dayjs` instead.
 */
export const zonedDifferenceInWeeks = (fromDate, toDate) =>
  differenceInWeeks(zonedDate(fromDate), zonedDate(toDate), {
    roundingMethod: 'ceil',
  });

/**
 * @deprecated Use `@harvestiq/dayjs` instead. If not applicable, move to `@harvestiq/utils`.
 */
export const convertTime = ({
  value,
  sourceUnit = 'h',
  targetUnit = 's',
} = {}) => {
  // avoid division by zero
  if (!value || !sourceUnit || !targetUnit) {
    console.trace(
      `Invalid params: { value: %s, sourceUnit: '%s', targetUnit: '%s' }`,
      value,
      sourceUnit,
      targetUnit
    );
    return null;
  }

  const timeUnitsInSeconds = {
    ms: 0.001, // milliseconds
    s: 1, // seconds
    m: 60, // minutes
    h: 3600, // hours
    d: 86400, // days
  };

  const source = timeUnitsInSeconds[sourceUnit];
  const target = timeUnitsInSeconds[targetUnit];

  if (!source || !target) {
    console.trace('Unknown time unit');
    return null;
  }

  // Convert source to seconds
  const valueInSeconds = value * source;

  // Convert seconds to target unit
  return valueInSeconds / target;
};

/**
 * @deprecated Use `@harvestiq/dayjs` instead.
 */
export const formatZonedDate = (
  date = new Date(),
  tz = gmt0timezone,
  fmt = dateFormat
) => format(zonedDate(date, tz), fmt);

/**
 * @param {Date} date - The date to check.
 * @returns {boolean} True if the date is in daylight saving time.
 * @note It is useful to check CST and CDT dates.
 * @deprecated Use `@harvestiq/dayjs` instead.
 */
export const isDaylightSavingTime = (date = new Date()) => {
  if (!isDate(date)) {
    return false;
  }
  const jan = new Date(date.getFullYear(), 0, 1);
  const jul = new Date(date.getFullYear(), 6, 1);
  const timezoneOffset = Math.max(
    jan.getTimezoneOffset(),
    jul.getTimezoneOffset()
  );
  return date.getTimezoneOffset() < timezoneOffset;
};

export const formatSlackNotificationDate = (date = new Date()) => {
  const newCentralTimeDate = zonedDate(date, centralTimezone);
  const timezoneStr = isDaylightSavingTime(newCentralTimeDate) ? 'CDT' : 'CST';
  return `${format(newCentralTimeDate, longDateTimeFormat)} ${timezoneStr}`;
};

export const findClosestDate = (
  dateStringsList = [],
  targetDate = new Date()
) => {
  if (!dateStringsList?.length) {
    return null;
  }

  // sort dates in ascending order (so we keep the greatest after the target date) and find the closest date to targetDate
  return dateStringsList
    .sort((prev, curr) => {
      const prevDate = new Date(prev);
      const currDate = new Date(curr);

      return prevDate - currDate;
    })
    .reduce((prev, curr) => {
      const prevDiff = Math.abs(new Date(prev) - targetDate);
      const currDiff = Math.abs(new Date(curr) - targetDate);

      return prevDiff < currDiff ? prev : curr;
    }, dateStringsList[0]);
};
